/**
 * @file InviteUsersToPrivateChatModal.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-05-27
 * @description file containing InviteUsersToPrivateChatModal component
 */

import React, { useState, useEffect } from "react";
import { fetchPublicUsers } from "../../utils/usersUtils";
import { useUser } from "../../context/UserContext";
import { inviteUsersToPrivateChat } from "../../utils/privateChatUtils";

/**
 * This component is a modal (popup) that allows users to invite other users to a private chat - this appears when the InviteUsersToPrivateChatButton component is clicked
 * It displays a list of users that can be invited to the chat.
 * Users can be selected or removed from the list of invited users.
 * When the invite button is clicked, the selected users are invited to the chat.
 *
 * NOTE: need to handle preventing invites to users who already have a pending invitation to the chat
 *
 * @param {Object} props - The properties passed to the component
 * @param {Function} props.onClose - Function to close the modal
 * @param {Array} props.members - Array of users who are already members of the private chat
 * @param {Array} props.selectedPrivateChatPendingMembers - Array of users who have pending invitations to the private chat
 *
 * @returns {JSX.Element} The rendered InviteUsersToPrivateChatModal component
 */
const InviteUsersToPrivateChatModal = ({
  selectedPrivateChat,
  onClose,
  members,
  selectedPrivateChatPendingMembers,
}) => {
  const [invitedUsers, setInvitedUsers] = useState([]); // State variable for the list of invited users
  const [users, setUsers] = useState([]); // State variable for the list of users
  const [loading, setLoading] = useState(false); // State variable for loading state
  const [searchTerm, setSearchTerm] = useState(""); // State variable for search term

  const { user } = useUser();

  // Fetch users from the database when the component mounts
  useEffect(() => {
    const loadUsers = async () => {
      const users = await fetchPublicUsers();
      setUsers(users);
    };
    loadUsers();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Function to add a user to list of invited users
  const handleUserSelect = (invitee) => {
    if (invitee.uid !== user.uid) {
      setInvitedUsers((prev) => [...prev, invitee]);
    }
  };

  // Function to remove a user from the list of invited users
  const handleUserRemove = (invitee) => {
    setInvitedUsers((prev) => prev.filter((u) => u.uid !== invitee.uid));
  };

  const handleInviteUsers = async () => {
    setLoading(true);
    try {
      await inviteUsersToPrivateChat(
        selectedPrivateChat.id,
        selectedPrivateChat.name,
        selectedPrivateChat.icon,
        user.uid,
        invitedUsers,
      );
      setInvitedUsers([]); // Clear invited users after successful invite
      onClose(); // Close the modal
    } catch (error) {
      console.error("Error inviting users:", error);
      // Handle error (e.g., show notification)
    }
    setLoading(false);
  };

  return (
    <>
      {user && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[1005]">
          <div className="bg-secondary p-6 rounded-lg max-w-md w-full">
            <h2 className="text-xl mb-4 text-center font-semibold">
              Invite Users to '{selectedPrivateChat?.name}'
            </h2>

            {/* Search input for users */}
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-2 p-2 border rounded w-full text-sm"
            />

            <div className="mb-4 max-h-40 overflow-y-scroll">
              {filteredUsers.length === 0 ? (
                <div className="text-gray-500 text-center py-2">
                  {searchTerm
                    ? "No users found matching your search"
                    : "No users available"}
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.uid}
                    className="flex items-center justify-between py-2 mb-2 px-4 rounded-xl bg-base-100 hover:bg-base-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <div>
                        {user.profilePicture ? (
                          <img
                            src={user.profilePicture}
                            alt="User Icon"
                            className="w-11 h-11 mr-4 rounded-full object-cover border-2 border-gray-300 shadow-sm flex-shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 mr-4 rounded-full bg-gray-300 border-2 border-gray-300 shadow-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl font-bold text-gray-600">
                              {user.username
                                ? user.username.charAt(0).toUpperCase()
                                : "?"}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium">{user.username}</span>
                      </div>
                    </div>

                    {/* Invite/Remove Button */}
                    {selectedPrivateChatPendingMembers.some(
                      (u) => u.uid === user.uid,
                    ) ? (
                      <button
                        disabled={true}
                        className="text-gray-400 px-2 py-1 text-sm"
                      >
                        Pending
                      </button>
                    ) : members.some((m) => m.uid === user.uid) ? (
                      <>
                        <button
                          disabled={true}
                          onClick={() => handleUserRemove(user)}
                          className="text-gray-400 px-2 py-1 text-sm"
                        >
                          Member
                        </button>
                      </>
                    ) : invitedUsers.some((u) => u.uid === user.uid) ? (
                      <button
                        onClick={() => handleUserRemove(user)}
                        className="text-primary hover:text-primary-dark px-2 py-1 text-sm"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUserSelect(user)}
                        className="hover:text-primary px-2 py-1 text-sm"
                      >
                        Invite
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Show invited users count */}
            {invitedUsers.length > 0 && (
              <div className="mb-4 text-sm text-gray-600">
                ({invitedUsers.length}) user
                {invitedUsers.length !== 1 ? "s" : ""} invited
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="btn btn-neutral px-4 py-2 btn-sm"
              >
                Cancel
              </button>
              <button
                disabled={invitedUsers.length === 0 || loading}
                onClick={handleInviteUsers}
                className="btn btn-primary px-4 py-2 btn-sm"
              >
                {loading ? "Sending..." : "Send Invites"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteUsersToPrivateChatModal;
